import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

// Store
import { Dispatch, RootState } from '../../store/store';

// Components
import IconGearFilled from '../Icons/IconGearFilled/IconGear';
import { ElementSize } from '../../types/element-size';
import { HIDEABLE_OPTION_KEYS, HIDEABLE_OPTION_LABELS } from './option';

// This is a single, page-level control (not per-table) - it reads/writes the
// user's own hidden_cycle_table_columns preference directly, so dropping one
// instance at the top of a page applies to every ScreenCyclesTable rendered
// anywhere on that page (and every other page) at once, rather than needing
// one picker per table section.
type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const ColumnVisibilityMenu: React.FC<ComponentProps> = ({ user, updateHiddenCycleTableColumns }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const hiddenColumns = user?.hidden_cycle_table_columns ?? [];

  const handleToggle = (key: string) => {
    const newHiddenColumns = hiddenColumns.includes(key)
      ? hiddenColumns.filter((hiddenKey) => hiddenKey !== key)
      : [...hiddenColumns, key];

    updateHiddenCycleTableColumns(newHiddenColumns);
  };

  return (
    <div ref={containerRef} className="c-column-visibility-menu relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center gap-1.5 rounded border border-stroke px-2.5 py-1.5 text-xs font-medium text-slate-gray hover:border-primary hover:text-primary dark:border-strokedark dark:text-bodydark"
      >
        <IconGearFilled size={ElementSize.XXS} />
        Columns
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full z-20 mt-1 w-52 rounded border border-stroke bg-white p-2 text-left shadow-default dark:border-strokedark dark:bg-boxdark">
          {HIDEABLE_OPTION_KEYS.map((key) => (
            <label
              key={key}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-black hover:bg-gray-2 dark:text-white dark:hover:bg-meta-4"
            >
              <input type="checkbox" checked={!hiddenColumns.includes(key)} onChange={() => handleToggle(key)} />
              <span className="truncate">{HIDEABLE_OPTION_LABELS[key]}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  user: state.user.profile,
});

const mapDispatch = (dispatch: Dispatch) => ({
  updateHiddenCycleTableColumns: dispatch.user.updateHiddenCycleTableColumns,
});

export default connect(mapState, mapDispatch)(ColumnVisibilityMenu);
